import * as React from 'react';
import { styled } from '@linaria/react';
import { Pause, Play } from 'lucide-react';

import usePrefersReducedMotion from '@/hooks/use-prefers-reduced-motion';

import Demo, {
  DemoToggleButtonGroupControl,
} from '@/components/Demo';
import SvgWithGrid from '@/components/SvgWithGrid';

import { calculateEllipseCenter } from './Paths.helpers';

interface Props {
  variant?: 'with-arc' | 'without-arc';
}

function ArcSweepDemo({ variant = 'with-arc' }: Props) {
  const [status, setStatus] = React.useState<'playing' | 'paused'>(
    'paused'
  );

  const prefersReducedMotion = usePrefersReducedMotion();

  React.useEffect(() => {
    // On mount AND when the PRM status changes, pause or play the animation:
    setStatus(prefersReducedMotion ? 'paused' : 'playing');
  }, [prefersReducedMotion]);

  return (
    <Demo
      outputWrapper={null}
      initialValues={{
        direction: 'ccw',
      }}
      customGrid={{
        gridTemplateColumns: '1fr',
        justifyContent: 'center',
      }}
      controlPosition="top"
      controls={[
        <DemoToggleButtonGroupControl
          key="direction"
          id="direction"
          label="Direction"
          options={[
            { label: 'Counter-clockwise', value: 'ccw' },
            { label: 'Clockwise', value: 'cw' },
          ]}
        />,
      ]}
    >
      {({ direction }) => {
        const { cx, cy } = calculateEllipseCenter(
          { x: 16, y: 10 },
          { x: 16, y: 22 },
          8,
          8,
          1,
          direction === 'cw' ? 0 : 1
        );

        return (
          <Wrapper
            onClick={() =>
              setStatus(status === 'playing' ? 'paused' : 'playing')
            }
          >
            <Svg gridGap={2} lineWidth={0.1} viewBox={`0 0 32 32`}>
              <Ellipse
                fill="none"
                cx={cx}
                cy={cy}
                rx={8}
                ry={8}
                stroke="white"
                strokeWidth={0.5}
                strokeDasharray="1 1"
                style={{
                  animationPlayState:
                    status === 'playing' ? 'running' : 'paused',
                  '--direction':
                    direction === 'cw' ? 'normal' : 'reverse',
                  transformOrigin: `${cx}px ${cy}px`,
                }}
              />
              <path
                style={{
                  stroke: 'hsl(260deg 70% 65%)',
                  strokeWidth: 1,
                  fill: 'none',
                }}
                d={
                  variant === 'with-arc'
                    ? `
                  M 16,0
                  L 16,10
                  A 8,8 0 0 ${direction === 'cw' ? 1 : 0} 16,22
                  L 16,32
                  `
                    : `
                  M 16,0
                  L 16,10
                  M 16,22
                  L 16,32
                  `
                }
              />
              <circle
                r={0.625}
                cx={16}
                cy={10}
                fill="white"
                stroke="var(--color-background)"
                strokeWidth={0.25}
              />
              <circle
                r={0.625}
                cx={16}
                cy={22}
                fill="white"
                stroke="var(--color-background)"
                strokeWidth={0.25}
              />
            </Svg>
            <PauseButton>
              {status === 'playing' ? <Pause /> : <Play />}
            </PauseButton>
          </Wrapper>
        );
      }}
    </Demo>
  );
}

const Wrapper = styled.div`
  position: relative;
  width: 100%;
  max-width: 300px;
  margin-inline: auto;
  padding-block: 16px;
`;

const Svg = styled(SvgWithGrid)`
  display: block;
  aspect-ratio: 1;
  border-radius: 4px;
  outline: 1px solid var(--color-gray-300);
`;

const Ellipse = styled.ellipse`
  fill: none;
  stroke: white;
  stroke-width: 0.5;
  stroke-dasharray: 1 1;
  animation: spin 10000ms linear infinite var(--direction);
`;

const PauseButton = styled.button`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  margin: auto;
  width: 64px;
  height: 64px;
  color: white;
  display: flex;
  justify-content: center;
  align-items: center;
  background: rgba(0, 0, 0, 0.5);
  border-radius: 50%;
  transition: opacity 500ms;
  cursor: pointer;
  opacity: 0;

  ${Wrapper}:hover &, &:focus-visible {
    opacity: 1;
  }
`;

export default ArcSweepDemo;
